<script setup lang="ts">
import { computed, inject, reactive, toRefs } from 'vue'

import AssortmentItem from '@/components/AssortmentItem.vue'
import BrandImage from '@/components/BrandImage.vue'
import DateItem from '@/components/DateItem.vue'

import convertToUTC from '@/lib/convertToUTC'
import type { DateEvent, Event, Store } from '@/types'

type StoreInfoPlace = (Event & Store) | null

const props = defineProps<{ place: StoreInfoPlace }>()
const state = reactive({
  showShareBar: false,
})

const { place } = toRefs(props)

const brand = inject('BRAND')

const formattedDate = computed(() => {
  const events = place.value?.properties?.events ?? []
  const firstValid = events.find((event) => event.startAt)

  return firstValid ? convertToUTC(firstValid.startAt, 'dd.MM.yyyy HH:mm') : ''
})

const remainingEvents = computed(() => {
  if (!place.value?.properties.events) {
    return [] as DateEvent[]
  }

  return place.value.properties.events.slice(
    1,
    place.value.properties.events.length,
  ) as unknown as DateEvent[]
})

const date = computed(() => formattedDate.value.split(' ')[0])
const time = computed(() => formattedDate.value.split(' ')[1])
</script>

<template>
  <div
    v-if="props.place"
    class="flex flex-col gap-2 px-5 pb-4 pt-5 font-sans md:gap-0"
  >
    <div v-if="!state.showShareBar" class="flex items-center gap-4 pb-3">
      <a
        :href="`https://www.google.com/maps?q=${props.place.geometry.coordinates.lat},${props.place.geometry.coordinates.lng}`"
        target="_blank"
      >
        <BrandImage
          src="icons/Route.svg"
          alt="route"
          width="30"
          class="hover:cursor-pointer"
        />
      </a>
      <div v-if="props.place.properties.website">
        <a :href="`${props.place.properties.website}`" target="_blank">
          <BrandImage
            src="icons/Web.svg"
            alt="web"
            width="24"
            class="hover:cursor-pointer"
          />
        </a>
      </div>
    </div>
    <div
      v-if="props.place.properties.name"
      class="text-md flex items-center font-sans text-black"
      :class="{
        'md:text-md font-semibold': brand === 'satch',
        'font-sans-semi-bold md:text-base': brand !== 'satch',
        'md:pb-4': !place?.properties.events,
      }"
    >
      <p>{{ props.place.properties.name }}</p>
    </div>
    <div
      v-if="props.place.properties.addressSuffix"
      class="text-base font-sans text-primary"
    >
      <h2 class="font-sans-bold font-bold">{{ $t("addressSuffix") }}:</h2>
      <p>
        {{ props.place.properties.addressSuffix }}
      </p>
    </div>
    <div
      v-if="
        props.place.properties.events &&
        props.place.properties.events.filter(Boolean).length >= 1
      "
      class="flex flex-col gap-0 pt-0 font-medium text-black text-base md:gap-1 md:pt-2 md:font-normal"
    >
      <template v-if="date && time">
        <p>{{ $t("nextEvent") }}:</p>
        <div class="flex items-center font-medium text-black last:pb-4">
          <p>{{ date }}, {{ time }} {{ $t("time") }}</p>
        </div>
      </template>
      <div
        v-if="
          props.place.properties.sprayer &&
          props.place.properties.sprayer.filter(Boolean).length >= 1
        "
      >
        <div
          v-for="(sprayer, index) in props.place.properties.sprayer"
          :key="index"
        >
          <p>Sprayer: {{ sprayer }}</p>
        </div>
      </div>
      <div v-if="remainingEvents.length >= 1">
        <p class="pt-2.5">{{ $t("moreEvents") }}:</p>
        <DateItem
          v-for="(event, index) in remainingEvents"
          :key="index"
          :event="event"
        />
      </div>
    </div>
    <div class="flex flex-col gap-2 text-base md:text-sm pt-5">
      <div
        class="flex items-center gap-5 rounded-xl border px-3 py-2 hover:bg-white"
        :class="{
          'border-gray-light md:py-1': brand === 'satch',
          'border-gray-300 md:py-3': brand !== 'satch',
        }"
      >
        <BrandImage src="icons/Location.svg" alt="location" width="24" />
        <div class="truncate">
          <p
            v-if="props.place.properties.addressSuffix"
            class="text-base text-primary font-bold"
          >
            {{ $t("addressSuffix") }}:
          </p>
          <p v-if="props.place.properties.address" class="truncate">
            {{ props.place.properties.address }}
          </p>
          <p v-else-if="props.place.properties.streetNr" class="truncate">
            {{ props.place.properties.streetNr }}
          </p>
          <p
            :class="{
              'text-gray-500': brand !== 'satch',
            }"
          >
            {{ props.place.properties.zip }}, {{ props.place.properties.city }},
            {{ props.place.properties.country[0] }}
          </p>
        </div>
      </div>
      <a :href="`tel:${props.place.properties.phone}`" target="_blank">
        <div
          v-if="props.place.properties.phone"
          class="flex items-center gap-5 rounded-xl border px-3 py-2 hover:cursor-pointer hover:bg-white md:py-1"
          :class="{
            'border-gray-light': brand === 'satch',
            'border-gray-300': brand !== 'satch',
          }"
        >
          <BrandImage
            src="icons/Phone.svg"
            alt="phone"
            width="24"
            :class="{
              'border-gray-light': brand === 'satch',
              'border-gray-300': brand !== 'satch',
            }"
          />
          <div class="truncate">
            <p class="truncate">{{ props.place.properties.phone }}</p>
            <p
              :class="{
                'text-gray-medium': brand === 'satch',
                'text-gray-500': brand !== 'satch',
              }"
            >
              {{ $t("storePhone") }}
            </p>
          </div>
        </div>
      </a>
      <a
        v-if="props.place.properties.mail"
        :href="`mailto:${props.place.properties?.mail}`"
        target="_blank"
        class="flex items-center gap-5 rounded-xl border px-3 py-2 hover:bg-white md:py-1"
        :class="{
          'border-gray-light': brand === 'satch',
          'border-gray-300': brand !== 'satch',
        }"
      >
        <BrandImage src="icons/Email.svg" alt="email" width="24" />
        <div class="truncate">
          <p class="truncate">{{ props.place.properties.mail }}</p>
          <p
            class="text-s"
            :class="{
              'text-gray-medium': brand === 'satch',
              'text-gray-500': brand !== 'satch',
            }"
          >
            {{ $t("storeEmail") }}
          </p>
        </div>
      </a>
    </div>

    <div
      v-if="props.place.properties.assortment"
      class="mt-2 flex items-center gap-5 rounded-xl border px-3 py-2 hover:bg-white md:py-1"
      :class="{
        'border-gray-light': brand === 'satch',
        'border-gray-300': brand !== 'satch',
      }"
    >
      <BrandImage src="icons/Filter.svg" alt="filter" width="24" />
      <div class="w-full">
        <div
          class="flex w-full"
          :class="{
            'flex-col':
              props.place.properties.assortment.translation.length >= 3,
            'gap-1': props.place.properties.assortment.translation.length < 3,
          }"
        >
          <AssortmentItem
            v-for="(assortment, i) in props.place.properties.assortment
              .translation"
            :key="i"
            :assortment="assortment"
            :is-last-element="
              i == props.place.properties.assortment.translation.length - 1
            "
          />
        </div>
        <p class="text-gray-medium text-sm">
          {{ $t("storeType") }}
        </p>
      </div>
    </div>
  </div>
</template>
